import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useAction, usePaginatedQuery } from 'convex/react'
import { Link } from 'react-router-dom'
import { Loader2, ChevronRight, Play, CheckCircle2, AlertCircle, Copy, Check, Sparkles, FlaskConical } from 'lucide-react'
import { api } from '../../../../convex/_generated/api'
import { Id } from '../../../../convex/_generated/dataModel'
import { useAuth } from '@/contexts/auth-context'

type ContentFilter = 'all' | 'hasClips' | 'hasGenerated' | 'hasExported'

const CONTENT_FILTERS: { value: ContentFilter; label: string }[] = [
  { value: 'all', label: 'All Projects' },
  { value: 'hasClips', label: 'Has Clips' },
  { value: 'hasGenerated', label: 'Has Generated Content' },
  { value: 'hasExported', label: 'Exported' },
]

const TIME_WINDOWS: { value: number | 'all'; label: string }[] = [
  { value: 1, label: 'Last 24 hours' },
  { value: 3, label: 'Last 3 days' },
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
]

// Per-session pipeline cost breakdown (rough averages):
//   - Gemini 2.5 Flash video analysis (only for exported videos, ~30% of sessions): ~$0.005/session avg
//   - Claude Opus 4.6 session agent (5–8 iterations on average): ~$0.70/session
//   - Opus 4.6 cluster pass: ~$0.015/session (amortized across the batch)
//   - Sonnet 4.6 per-issue enrichment (~0.75 issues/session): ~$0.025/session
// Real cost varies with session length, prompt size and issue count.
const COST_FULL_PIPELINE_USD = 0.75 // session analysis (incl. Gemini) + cluster + enrichment
const COST_META_ONLY_USD = 0.04 // cluster + enrichment when sessions are already analyzed
const RUN_CAP = 500

function formatCost(usd: number) {
  if (usd >= 100) return `$${usd.toFixed(0)}`
  return `$${usd.toFixed(2)}`
}

export default function AdminMetaAnalysisRunsPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const runs = useQuery(api.admin.listMetaAnalysisRuns, isAdmin ? {} : 'skip')
  const runMetaAnalysis = useAction(api.admin.runMetaAnalysis)
  const scheduleSessionAnalyses = useAction(api.admin.scheduleSessionAnalyses)
  const createRun = useMutation(api.admin.createMetaAnalysisRun)
  const updateRun = useMutation(api.admin.updateMetaAnalysisRun)

  // Page size is bounded by Convex's 16 MiB read-bytes budget per query.
  // sessionAnalysis (rich findings v7) makes each workflow doc large enough that
  // ~500/page blows the limit; 50 keeps headroom even with a few oversized blobs.
  const projectsPager = usePaginatedQuery(
    api.admin.listProjectsForMetaPage,
    isAdmin ? {} : 'skip',
    { initialNumItems: 50 },
  )

  // Auto-page through every workflow page so the count reflects the full dataset.
  useEffect(() => {
    if (projectsPager.status === 'CanLoadMore') {
      projectsPager.loadMore(50)
    }
  }, [projectsPager.status, projectsPager.loadMore])

  const [contentFilter, setContentFilter] = useState<ContentFilter>('all')
  const [daysBack, setDaysBack] = useState<number | 'all'>(7)
  const [excludeAdmins, setExcludeAdmins] = useState(true)
  const [running, setRunning] = useState(false)
  const [scheduling, setScheduling] = useState(false)
  const [scheduledNotice, setScheduledNotice] = useState<string | null>(null)
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null)
  const [copiedRunId, setCopiedRunId] = useState<string | null>(null)

  const filtered = useMemo(() => {
    if (!projectsPager.results) return { allIds: [], analyzedIds: [], unanalyzedIds: [] } as {
      allIds: Id<'workflows'>[]
      analyzedIds: Id<'workflows'>[]
      unanalyzedIds: Id<'workflows'>[]
    }
    const cutoff = daysBack === 'all' ? 0 : Date.now() - daysBack * 24 * 60 * 60 * 1000
    const allIds: Id<'workflows'>[] = []
    const analyzedIds: Id<'workflows'>[] = []
    const unanalyzedIds: Id<'workflows'>[] = []
    for (const p of projectsPager.results) {
      if (excludeAdmins && p.isAdmin) continue
      if (cutoff && (p.createdAt ?? 0) < cutoff) continue
      let matches = false
      switch (contentFilter) {
        case 'hasClips': matches = p.hasClips; break
        case 'hasGenerated': matches = p.hasGenerated; break
        case 'hasExported': matches = p.hasExported; break
        default: matches = true
      }
      if (!matches) continue
      const id = p._id as Id<'workflows'>
      allIds.push(id)
      if (p.isAnalyzed) analyzedIds.push(id)
      else unanalyzedIds.push(id)
    }
    return { allIds, analyzedIds, unanalyzedIds }
  }, [projectsPager.results, contentFilter, daysBack, excludeAdmins])

  const matchCount = filtered.allIds.length
  const analyzedCount = filtered.analyzedIds.length
  const unanalyzedCount = filtered.unanalyzedIds.length
  const cappedCluster = Math.min(analyzedCount, RUN_CAP)
  // Pipeline cost: pay full pipeline for unanalyzed (session analysis + Gemini + cluster + enrichment),
  // pay meta-only for already-analyzed (cluster + enrichment).
  const fullPipelineCost = unanalyzedCount * COST_FULL_PIPELINE_USD + analyzedCount * COST_META_ONLY_USD
  const stillScanning =
    projectsPager.status === 'CanLoadMore' ||
    projectsPager.status === 'LoadingMore' ||
    projectsPager.status === 'LoadingFirstPage'
  const totalScanned = projectsPager.results?.length ?? 0

  const handleRun = async () => {
    if (cappedCluster === 0 || running) return
    setRunning(true)
    setScheduledNotice(null)
    const workflowIds = filtered.analyzedIds.slice(0, RUN_CAP)
    const runId = await createRun({ workflowIds })
    runMetaAnalysis({ workflowIds, runId })
      .catch(async (err) => {
        await updateRun({ runId, status: 'failed', error: err?.message || 'Unknown error' })
      })
      .finally(() => setRunning(false))
  }

  const handleScheduleAnalyses = async () => {
    if (unanalyzedCount === 0 || scheduling) return
    setScheduling(true)
    setScheduledNotice(null)
    try {
      const result = await scheduleSessionAnalyses({ workflowIds: filtered.unanalyzedIds })
      setScheduledNotice(
        `Scheduled session analysis on ${result.scheduled} project${result.scheduled === 1 ? '' : 's'}. ` +
        `Re-run cluster after they finish (a few minutes).`,
      )
    } catch (err: any) {
      setScheduledNotice(`Failed: ${err?.message || 'unknown error'}`)
    } finally {
      setScheduling(false)
    }
  }

  const copyPrd = (runId: string, prd: string) => {
    navigator.clipboard.writeText(prd)
    setCopiedRunId(runId)
    setTimeout(() => setCopiedRunId(null), 2000)
  }

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div>
        <h1 className="text-lg font-semibold text-neutral-900">Meta Analysis</h1>
        <p className="text-sm text-neutral-500 mt-0.5">Cluster issues across analyzed sessions</p>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" />
            <h2 className="text-sm font-semibold text-neutral-900">Run new analysis</h2>
          </div>
          <p className="text-xs text-neutral-500 mt-0.5 ml-6">
            Pick the slice of projects to analyze and cluster.
            {stillScanning && (
              <span className="ml-2 text-neutral-400 inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                scanning {totalScanned} so far…
              </span>
            )}
          </p>
        </div>

        <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <FilterField label="Content">
            <Select value={contentFilter} onChange={(v) => setContentFilter(v as ContentFilter)}>
              {CONTENT_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </Select>
          </FilterField>
          <FilterField label="Time window">
            <Select
              value={String(daysBack)}
              onChange={(v) => setDaysBack(v === 'all' ? 'all' : Number(v))}
            >
              {TIME_WINDOWS.map((w) => (
                <option key={String(w.value)} value={String(w.value)}>{w.label}</option>
              ))}
            </Select>
          </FilterField>
          <FilterField label="Audience">
            <label className="flex items-center gap-2 h-9 px-3 rounded-md border border-neutral-200 bg-white text-sm text-neutral-700 cursor-pointer hover:border-neutral-300 transition-colors">
              <input
                type="checkbox"
                checked={excludeAdmins}
                onChange={(e) => setExcludeAdmins(e.target.checked)}
                className="rounded border-neutral-300 text-neutral-900 focus:ring-neutral-300"
              />
              Exclude admin users
            </label>
          </FilterField>
        </div>

        <div className="border-t border-neutral-100 bg-neutral-50/40 px-5 py-4 flex items-end justify-between gap-6 flex-wrap">
          <div className="flex items-end gap-6">
            <Stat
              label="Projects"
              value={matchCount.toString()}
              hint={stillScanning ? 'updating…' : undefined}
            />
            <div className="h-8 w-px bg-neutral-200" />
            <Stat
              label="Analyzed"
              value={analyzedCount.toString()}
              hint={
                analyzedCount > RUN_CAP
                  ? `cluster capped at ${RUN_CAP}`
                  : unanalyzedCount > 0
                    ? `${unanalyzedCount} need analysis`
                    : 'ready to cluster'
              }
              valueClass={analyzedCount === 0 ? 'text-neutral-400' : 'text-neutral-900'}
            />
            <div className="h-8 w-px bg-neutral-200" />
            <Stat
              label="Estimated cost"
              value={`~${formatCost(fullPipelineCost)}`}
              hint={`full pipeline · ${formatCost(COST_FULL_PIPELINE_USD)}/new session`}
            />
          </div>
          <div className="flex items-center gap-2">
            {unanalyzedCount > 0 && (
              <button
                onClick={handleScheduleAnalyses}
                disabled={scheduling}
                className="flex items-center gap-1.5 text-sm font-medium px-3.5 py-2.5 rounded-lg border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50 disabled:opacity-40 transition-colors"
                title={`Schedule per-session analysis on ${unanalyzedCount} unanalyzed project${unanalyzedCount === 1 ? '' : 's'}`}
              >
                {scheduling
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <FlaskConical className="h-4 w-4" />}
                {scheduling ? 'Scheduling…' : `Analyze ${unanalyzedCount}`}
              </button>
            )}
            <div className="flex flex-col items-end gap-0.5">
              <button
                onClick={handleRun}
                disabled={cappedCluster === 0 || running}
                className="flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                title={cappedCluster === 0 ? 'No analyzed sessions in this slice' : undefined}
              >
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                {running
                  ? 'Clustering…'
                  : cappedCluster === 0
                    ? 'Nothing to cluster'
                    : `Cluster ${cappedCluster}`}
              </button>
              {cappedCluster === 0 && unanalyzedCount > 0 && (
                <span className="text-[11px] text-neutral-400">analyze sessions first ↑</span>
              )}
            </div>
          </div>
        </div>
        {scheduledNotice && (
          <div className="px-5 py-3 text-xs text-blue-700 bg-blue-50 border-t border-blue-100">
            {scheduledNotice}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-neutral-900 mb-3">Runs</h2>
        {!runs ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-neutral-400" /></div>
        ) : runs.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-6">No runs yet.</p>
        ) : (
          <div className="rounded-lg border border-neutral-200 bg-white divide-y divide-neutral-100 overflow-hidden">
            {runs.map((run) => {
              const r = run as any
              const isExpanded = expandedRunId === run._id
              return (
                <div key={run._id}>
                  <button
                    onClick={() => setExpandedRunId(isExpanded ? null : run._id)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50/60 text-left transition-colors"
                  >
                    {run.status === 'running' && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500 shrink-0" />}
                    {run.status === 'completed' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                    {run.status === 'failed' && <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                    <span className="text-sm text-neutral-900 font-medium">{new Date(run.createdAt).toLocaleString()}</span>
                    <span className="text-sm text-neutral-400">— {run.config.projectCount} sessions</span>
                    {run.status !== 'completed' && <span className="text-xs text-neutral-400">({run.status})</span>}
                    {r.complaintsFound != null && (
                      <span className="text-xs text-neutral-400">· {r.complaintsFound} complaints</span>
                    )}
                    <div className="flex-1" />
                    <Link
                      to={`/admin/issues?run=${run._id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-blue-500 hover:underline"
                    >
                      View issues
                    </Link>
                    <ChevronRight className={`h-3.5 w-3.5 text-neutral-300 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>
                  {isExpanded && (
                    <div className="border-t border-neutral-100 bg-neutral-50/40">
                      {run.status === 'running' && (
                        <div className="flex items-center justify-center gap-2 py-6 text-neutral-500 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" /> Running analysis…
                        </div>
                      )}
                      {run.status === 'failed' && (
                        <div className="px-4 py-3 text-sm text-red-600">{r.error || 'Unknown error'}</div>
                      )}
                      {run.status === 'completed' && r.prd && (
                        <div>
                          <div className="flex items-center justify-end px-4 pt-2">
                            <button
                              onClick={() => copyPrd(run._id, r.prd!)}
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-neutral-100 text-neutral-700 hover:bg-neutral-200 transition-colors"
                            >
                              {copiedRunId === run._id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                              {copiedRunId === run._id ? 'Copied' : 'Copy PRD'}
                            </button>
                          </div>
                          <pre className="px-4 pb-4 pt-2 text-xs text-neutral-800 whitespace-pre-wrap font-mono leading-relaxed max-h-[500px] overflow-y-auto">{r.prd}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1.5 font-medium">{label}</div>
      {children}
    </div>
  )
}

function Select({
  value, onChange, children,
}: {
  value: string
  onChange: (v: string) => void
  children: React.ReactNode
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 text-sm border border-neutral-200 rounded-md px-2.5 bg-white hover:border-neutral-300 focus:outline-none focus:ring-1 focus:ring-neutral-300 focus:border-neutral-300 transition-colors"
    >
      {children}
    </select>
  )
}

function Stat({
  label, value, hint, valueClass,
}: {
  label: string
  value: string
  hint?: string
  valueClass?: string
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">{label}</div>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        <span className={`text-2xl font-semibold tabular-nums ${valueClass ?? 'text-neutral-900'}`}>{value}</span>
        {hint && <span className="text-[11px] text-neutral-400">{hint}</span>}
      </div>
    </div>
  )
}
